/**
 * Module Modal - TICKET ZKPVN1
 * 
 * Modal para crear/editar módulos.
 */

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const ModuleModal = ({ module, existingIds, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    moduleId: '',
    title: '',
    description: ''
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (module) {
      setFormData({
        moduleId: module.moduleId,
        title: module.title,
        description: module.description || ''
      });
    }
  }, [module]);

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    // Validaciones
    if (!formData.title || formData.title.trim() === '') {
      setError('El título es requerido');
      return;
    }

    // Si es nuevo módulo, validar que el ID no exista
    if (!module) {
      const moduleId = formData.moduleId || generateSlug(formData.title);
      if (existingIds.includes(moduleId)) {
        setError(`Ya existe un módulo con ID: ${moduleId}`);
        return;
      }
    }

    onSave({
      moduleId: formData.moduleId,
      title: formData.title.trim(),
      description: formData.description.trim()
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {module ? 'Editar módulo' : 'Crear módulo'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Module ID (solo en creación) */}
          {!module && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID del módulo <span className="text-gray-400">(opcional, se autogenera)</span>
              </label>
              <input
                type="text"
                value={formData.moduleId}
                onChange={(e) => setFormData({ ...formData, moduleId: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="empathy-strategic"
              />
              <p className="text-xs text-gray-500 mt-1">
                Si lo dejas vacío, se generará automáticamente desde el título
              </p>
            </div>
          )}

          {/* Module ID (readonly en edición) */}
          {module && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID del módulo
              </label>
              <input
                type="text"
                value={formData.moduleId}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                El ID no se puede cambiar para evitar referencias rotas
              </p>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Título *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Empatía estratégica"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción <span className="text-gray-400">(opcional)</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Comprender problemas reales desde la perspectiva del usuario..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {module ? 'Guardar cambios' : 'Crear módulo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModuleModal;
